import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import { Hamburger, Media, Search } from '@syconium/little-miss-figgy'

import {
  PrimaryNavMenuDialogProvider,
  PrimaryNavMenuDialogTrigger,
} from '../../../app/_components/chrome/PrimaryNavMenus.client'
import {
  PrimaryNavSearchForm,
  PrimaryNavSearchMenuBody,
  PrimaryNavSearchMenuDialog,
} from '../../../app/_components/chrome/PrimaryNavSearch.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useModalState } from '../../modals/useModalState'
import { NavModal } from '../NavModal'

import { NavLogo } from './NavLogo'
import { HamburgerButton, Left, LogoMedia, NavGhostProxy } from './styles'

/**
 * The left container for the NavHeader.
 *
 * On mobile viewports, renders the hamburger button and the NavModal.
 * On desktop viewports, renders the brand logo.
 *
 * @param props
 * @param [props.isFadedOut] - If the contents in the container should be opaque.
 * @param [props.isNavModalOpen] - If the NavModal should be open.
 * @param props.onNavModalOpen - Handler for when the nav modal is prompted to be open.
 * @param props.onNavModalClose- Handler for when the nav modal is prompted to be closed.
 *
 */
export function NavHeaderLeft({ isFadedOut = false }: { isFadedOut?: boolean }) {
  const {
    magnolia: { navigation: strings },
  } = useTranslation()

  const { isOpen, openModal, closeModal } = useModalState()

  return (
    <NavGhostProxy>
      <Left $isFadedOut={isFadedOut}>
        <LogoMedia greaterThanOrEqual='md'>
          <NavLogo />
        </LogoMedia>

        <Media lessThan='md'>
          <HamburgerButton
            aria-expanded={isOpen}
            aria-label={strings.hamburgerAriaLabel}
            data-testid='nav-header-hamburger-button'
            id='nav-header-hamburger-button'
            onClick={openModal}
            variant={'black-on-transparent'}
          >
            <Hamburger />
          </HamburgerButton>

          <NavModal isOpen={isOpen} onClose={closeModal} />
        </Media>
        <Media lessThan='md'>
          <VariantPrimaryNavSearchTrigger />
        </Media>
      </Left>
    </NavGhostProxy>
  )
}

export const VariantPrimaryNavSearchTrigger = () => {
  const [open, setOpen] = useState(false)
  const translations = useTranslation()
  const searchText = translations.search.inputPlaceholder
  const noResultsText = translations.search.noResultsForQuery
  const suggestedSearchesTitleText = translations.search.popularSearchTitle
  const numberOfResultsText = translations.search.resultsForQuery
  const closeText = translations.magnolia.general.close
  const router = useRouter()

  const closeDialog = useCallback(() => setOpen(false), [])

  useEffect(() => {
    router.events.on('routeChangeStart', closeDialog)
    return () => router.events.off('routeChangeStart', closeDialog)
  }, [closeDialog, router.events])

  return (
    <PrimaryNavMenuDialogProvider open={open} setOpen={setOpen}>
      <PrimaryNavMenuDialogTrigger>
        <Search aria-label={searchText} />
      </PrimaryNavMenuDialogTrigger>
      <PrimaryNavSearchMenuDialog
        hideDismiss={{ sm: true, md: false }}
        closeText={closeText}
        aria-label={searchText}
      >
        <PrimaryNavSearchMenuBody>
          <PrimaryNavSearchForm
            searchText={searchText}
            noResultsText={noResultsText}
            suggestedSearchesTitleText={suggestedSearchesTitleText}
            numberOfResultsText={numberOfResultsText}
            previewSearchResultsOnType
          />
        </PrimaryNavSearchMenuBody>
      </PrimaryNavSearchMenuDialog>
    </PrimaryNavMenuDialogProvider>
  )
}
